import { create } from 'zustand';
import { MODES } from '@scio3/shared-types';

/** Subset of MODES used in schedule sidebar */
export type ScheduleSidebarMode = typeof MODES.DEFAULT | typeof MODES.SHARE_WITH_ME;

/** GAP-03: Search scope controls whether search queries current folder or all schedules */
export type SearchScope = 'all' | 'folder';

interface ScheduleSidebarState {
  searchText: string;
  mode: ScheduleSidebarMode;
  /** GAP-03: Whether to search all schedules or only within current folder */
  /**
   * Where a search looks. `folder` (the default) confines it to the location you are standing in;
   * `all` widens it to the whole account. Only meaningful inside a folder or a virtual location —
   * at the root the two are the same thing.
   *
   * Folder-first because navigating into a folder IS an expression of where you want to look.
   * Widening is one click on the scope chip in the search field, and it navigates out rather than
   * searching wide in place, so the location never contradicts the results. Mirrors Screens.
   */
  searchScope: SearchScope;
  /** When true, show only the current user's favorited schedules + folders (server-side). */
  favoritesOnly: boolean;
  /** Signal to scroll sidebar to a specific schedule after breadcrumb navigation */
  scrollToItemId: string | null;
  /** Whether the active schedule is currently visible in the sidebar scroll area */
  isItemVisible: boolean;
  setSearchText: (text: string) => void;
  setMode: (mode: ScheduleSidebarMode) => void;
  setSearchScope: (scope: SearchScope) => void;
  setFavoritesOnly: (favoritesOnly: boolean) => void;
  clearSearch: () => void;
  requestScrollToItem: (id: string) => void;
  clearScrollToItem: () => void;
  setItemVisible: (visible: boolean) => void;
}

export const useScheduleSidebarStore = create<ScheduleSidebarState>((set, get) => ({
  searchText: '',
  mode: MODES.DEFAULT,
  searchScope: 'folder',
  favoritesOnly: false,
  scrollToItemId: null,
  isItemVisible: false,
  setSearchText: (searchText) => set({ searchText }),
  setMode: (mode) => set({ mode, searchText: '', searchScope: 'folder', favoritesOnly: false }),
  setSearchScope: (searchScope) => set({ searchScope }),
  setFavoritesOnly: (favoritesOnly) => set({ favoritesOnly }),
  // Scope goes back to 'folder', not 'all' — clearing ends the search, so a scope widened for it
  // ends too, otherwise the next query in the same folder would silently start account-wide.
  clearSearch: () =>
    set((s) => ({
      searchText: '',
      searchScope: 'folder',
      mode: s.mode === MODES.SHARE_WITH_ME ? MODES.SHARE_WITH_ME : MODES.DEFAULT,
    })),
  requestScrollToItem: (id) => set({ scrollToItemId: id }),
  clearScrollToItem: () => set({ scrollToItemId: null }),
  setItemVisible: (visible) => {
    if (get().isItemVisible !== visible) set({ isItemVisible: visible });
  },
}));
